import React from 'react';
import { useSelector } from 'react-redux';
import FormControl from '@mui/material/FormControl';
import InputBase from '@mui/material/InputBase';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import cuid from 'cuid';
import PropTypes from 'prop-types';
import useAssigneeList from '@/lib/hooks/useAssigneeList';
import translate from '@/lib/intl/translate';

function ConstituentAssigneeSelect({
  className,
  id,
  name,
  onChange,
  size,
  value,
}) {
  const [uid] = React.useState(id || cuid());
  const { assignees, isFetching } = useAssigneeList();

  const { currentUserId } = useSelector((state) => ({
    currentUserId: state.authentication?.userId,
  }));

  const menuItems = React.useMemo(
    () =>
      assignees.reduce(
        (arr, { userId, displayName }) =>
          userId === currentUserId
            ? arr
            : [...arr, { value: userId, label: `@${displayName}` }],
        [
          { value: 'all', label: translate('CONSTITUENT_ASSIGNED_TO_ALL') },
          {
            value: currentUserId,
            label: translate('CONSTITUENT_ASSIGNED_TO_ME'),
          },
          {
            value: 'assigned',
            label: translate('CONSTITUENT_ASSIGNEE_ASSIGNED'),
          },
          {
            value: 'unassigned',
            label: translate('CONSTITUENT_ASSIGNEE_UNASSIGNED'),
          },
        ],
      ),
    [assignees, currentUserId],
  );

  return (
    <FormControl size={size} className={className} disabled={isFetching}>
      <InputLabel
        className="whitespace-nowrap"
        id={`constituent-assignee-select-label-${uid}`}
      >
        {translate('CONSTITUENT_ASSIGNEE')}
      </InputLabel>
      <Select
        labelId={`constituent-assignee-select-label-${uid}`}
        id={`constituent-assignee-select-${uid}`}
        value={value}
        className="w-full"
        name={name}
        label={translate('CONSTITUENT_ASSIGNEE')}
        onChange={onChange}
        size={size}
        displayEmpty
      >
        {menuItems.map((item) => (
          <MenuItem key={item.value} value={item.value}>
            {item.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

ConstituentAssigneeSelect.propTypes = {
  className: PropTypes.string,
  id: PropTypes.string,
  name: PropTypes.string,
  onChange: PropTypes.func,
  size: InputBase.propTypes.size,
  value: PropTypes.string,
};

ConstituentAssigneeSelect.defaultProps = {
  className: '',
  id: null,
  name: 'userIds',
  onChange: () => {},
  size: 'small',
  value: 'all',
};

export default ConstituentAssigneeSelect;
